import { useLocation } from "react-router-dom";
import { toast } from "@volca/ui/lib/toast";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useEnv } from "@/core/env";
import { useFeatureFlagContext } from "@/core/feature-flags";
import { useImpersonation } from "@/core/impersonation";
import { roleLabel } from "@/user/constants";
import { useGetMyUser } from "@/user/query";

const SESSION_ROUTE_PARAMS = new Set([
  "business",
  "tab",
  "sort",
  "desc",
  "sheet",
  "approval_unit",
  "sheet_name",
  "selected",
  "google_business_modal",
]);

/** Keeps navigation context while excluding credentials and unknown query values. */
const sessionRoute = (pathname: string, search: string) => {
  const params = new URLSearchParams(search);
  for (const key of Array.from(params.keys())) {
    if (!SESSION_ROUTE_PARAMS.has(key)) params.delete(key);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

/** Supplies the same credential-free route to the Session panel and clipboard. */
export const useDevSession = () => {
  const { appEnv, apiUrl } = useEnv();
  const { pathname, search } = useLocation();
  const { user, signedInUser } = useAuth();
  const { data: userInfo } = useGetMyUser();
  const { selectedBusiness, businessId } = useBusiness();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const { overrides } = useFeatureFlagContext();
  const person = isImpersonating ? impersonatedUser : (userInfo ?? user);
  const session = {
    environment: appEnv,
    api: apiUrl,
    route: sessionRoute(pathname, search),
    signedInAs: signedInUser?.email ?? null,
    actingAs: isImpersonating ? (person?.email ?? null) : null,
    user: person?.email ?? null,
    role: person?.role ? roleLabel(person.role) : null,
    business: selectedBusiness?.name ?? person?.parentable_name ?? null,
    businessId: businessId ?? null,
    overrides,
  };
  const copyContext = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(session, null, 2));
      toast.success("Copied session context.");
    } catch {
      toast.error(
        "Couldn't copy session context. Open Session to select the details.",
      );
    }
  };
  return { session, copyContext };
};
